import { SaveOutlined } from '@ant-design/icons';
import { Alert, Input } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Sort } from 'src/icons/Sort.icon';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import {
  getAppointmentStatusMappings,
  getDoctorAppointmentStatus,
  updateMappings,
} from 'src/store/actions/appointment.action';
import { getOrgSetting } from 'src/store/actions/settings.action';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import { SuperAdminTable } from '../../../baseComponents/Table';

const AppointmentStatusMappingPage = () => {
  const dispatch = useDispatch();

  const { getActiveOrganizationsList } = useSelector(
    (state) => state?.organizationState
  );
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const [bulkCSVUploadEnabled, setBulkCSVUploadEnabled] = useState(
    currentPractitioner?.org_settings?.enable_bulk_appointment_upload ?? false
  );
  const {
    appointmentStatusMappingsLoading,
    appointmentStatusMappings,
    getDoctorAppointmentList,
  } = useSelector((state) => state?.appointmentState);

  const [selectedOrganizationUuid, setSelectedOrganizationUuid] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const [_selectedOrganizationName, setSelectedOrganizationName] =
    useState(null);
  const [_organizationDropdown, setOrganizationDropdown] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [updatedValues, setUpdatedValues] = useState({});
  const [tableLoader, setTableLoader] = useState(false);
  const [_organizationName, setOrganizationName] = useState(
    currentPractitioner?.org_id
  );

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
    dispatch(getDoctorAppointmentStatus());
  }, []);

  useEffect(() => {
    setTableLoader(appointmentStatusMappingsLoading);
  }, [appointmentStatusMappingsLoading]);

  useEffect(() => {
    dispatch(getAppointmentStatusMappings(selectedOrganizationUuid));
  }, [selectedOrganizationUuid, dispatch]);

  useEffect(() => {
    if (
      getActiveOrganizationsList &&
      Array.isArray(getActiveOrganizationsList)
    ) {
      let dataArr = [];
      getActiveOrganizationsList.forEach((key, _index) => {
        dataArr.push({
          value: key?.org_id,
          label: key?.org_name,
          org_uuid: key?.org_uuid,
        });
      });
      setOrganizationDropdown(dataArr);
    }
  }, [getActiveOrganizationsList]);

  useEffect(() => {
    if (appointmentStatusMappings) {
      let dataArr = [];
      getDoctorAppointmentList.forEach((status) => {
        dataArr.push({
          key: status['status_id'],
          mapping: appointmentStatusMappings[status['status_name']] ?? [
            status['status_name'],
          ],
          recognizedAppointmentStatusName: status['status_name'],
        });
      });
      setTableData(dataArr);

      dataArr = {};
      getDoctorAppointmentList.forEach((status) => {
        dataArr[status['status_name']] = appointmentStatusMappings[
          status['status_name']
        ] ?? [status['status_name']];
      });
      setUpdatedValues(dataArr);
    }
  }, [
    selectedOrganizationUuid,
    appointmentStatusMappings,
    getDoctorAppointmentList,
  ]);

  useEffect(() => {
    async function getOrgSettingData() {
      await dispatch(getOrgSetting(selectedOrganizationUuid)).then(
        (setting) => {
          setBulkCSVUploadEnabled(
            setting?.enable_bulk_appointment_upload ?? false
          );
        }
      );
    }
    getOrgSettingData();
  }, [selectedOrganizationUuid, dispatch]);

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUuid(newOrg);
        handleSelectChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSelectChange = (option) => {
    const orgUuid = option?.org_uuid;
    setSelectedOrganizationUuid(orgUuid);
    setSelectedOrganizationName(option?.label);
    setOrganizationName(option?.value);
  };

  const columnNameSorter = (a, b) => {
    return a.recognizedAppointmentStatusName.localeCompare(
      b.recognizedAppointmentStatusName
    );
  };

  const handleInputChange = (e, recognizedAppointmentStatusName) => {
    const newList = e.target.value.split(',');
    setUpdatedValues({
      ...updatedValues,
      [recognizedAppointmentStatusName]: newList,
    });
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Appointment status in Insight{' '}
          <Sort className={'sort-icon-appointment-status-mapping'} />
        </span>
      ),
      dataIndex: 'recognizedAppointmentStatusName',
      key: 'recognizedAppointmentStatusName',
      width: '50%',
      sorter: columnNameSorter,
    },
    {
      title: <span className="font-[700]"> Appointment status in CSV</span>,
      dataIndex: 'mapping',
      key: 'mapping',
      render: (text, record) => {
        return (
          <>
            <div className="flex flex-row">
              <div className="basis-1/2">
                <Input
                  value={
                    updatedValues[record.recognizedAppointmentStatusName] !==
                    undefined
                      ? updatedValues[
                          record.recognizedAppointmentStatusName
                        ].join(',')
                      : ''
                  }
                  onChange={(e) => {
                    handleInputChange(
                      e,
                      record.recognizedAppointmentStatusName
                    );
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setUpdatedValues((prevValues) => ({
                        ...prevValues,
                        [record.recognizedAppointmentStatusName]:
                          appointmentStatusMappings[
                            record.recognizedAppointmentStatusName
                          ] ?? [record.recognizedAppointmentStatusName],
                      }));
                    } else {
                      const trimmedList = e.target.value
                        .split(',')
                        .map((item) => item.trim());
                      // .filter((item) => item !== '');
                      setUpdatedValues({
                        ...updatedValues,
                        [record.recognizedAppointmentStatusName]: trimmedList,
                      });
                    }
                  }}
                />
              </div>
            </div>
          </>
        );
      },
      width: '50%',
    },
  ];

  const handleFormSubmit = async (_values) => {
    let payload = {
      settings: {
        appointment_status_mapping: updatedValues,
      },
    };
    dispatch(
      updateMappings(
        payload,
        selectedOrganizationUuid,
        'appointment status mapping'
      )
    );
  };

  return (
    <>
      <div>
        <Formik initialValues={{}} onSubmit={handleFormSubmit}>
          {({ handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-row sm:justify-between gap-4 lg:justify-between items-center">
                <div className="flex flex-col justify-center"></div>
                {bulkCSVUploadEnabled && (
                  <div className="flex justify-end">
                    <div className="flex justify-end items-end">
                      <button
                        type="submit"
                        className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                      >
                        <SaveOutlined
                          className={`mr-2`}
                          width="14"
                          height="14"
                          viewBox="0 0 16 16"
                        />{' '}
                        Save all
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {bulkCSVUploadEnabled ? (
                <div className="super-admin-table">
                  <div className="mt-5 ant-table rounded-xl shadow-xl">
                    <SuperAdminTable
                      className={`appointment-page-table`}
                      columns={columns}
                      data={tableData}
                      loading={tableLoader}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center p-4 min-h-[60vh]">
                  <Alert
                    message="Bulk CSV uploads are currently disabled for this organization."
                    type="info"
                    showIcon
                  />
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AppointmentStatusMappingPage;
